import { useTranslations } from "@/lib/useTranslations";
import { useNavigate } from "react-router-dom";

import { TopNav } from "@/components/ui/admin/TopNav";
import { TeamMemberInitialsAvatar } from "@/components/ui/TeamMemberInitialsAvatar";
import { formatTeamMemberName } from "@/core/services/types/article.types";
import { getUserRole, teamMemberRoleToStaffRole } from "@/types/auth";
import { useAuth } from "@/lib/auth";

interface HeaderProps {
  pathname: string;
  isMobileNavOpen: boolean;
  onMenuToggle: () => void;
  onNavClick: () => void;
}

export function Header({
  pathname,
  isMobileNavOpen,
  onMenuToggle,
  onNavClick,
}: HeaderProps) {
  const t = useTranslations("common");
  const tTeam = useTranslations("team");
  const navigate = useNavigate();
  const { signOut, profile, user } = useAuth();

  const displayName = profile
    ? formatTeamMemberName(profile)
    : (user?.email ?? t("admin"));
  const staffRole = profile?.role
    ? teamMemberRoleToStaffRole(profile.role)
    : getUserRole(user);
  const roleLabel = staffRole ? tTeam(`roles.${staffRole}`) : null;

  async function handleLogout() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <header className="shrink-0 border-b border-black/10 bg-whart-bg">
      <div className="flex h-14 items-center justify-between px-4 lg:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onMenuToggle}
            aria-expanded={isMobileNavOpen}
            aria-label={t("toggleNav")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-whart-text transition-colors hover:bg-zinc-50 lg:hidden"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-5 w-5"
            >
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          </button>
          <span className="text-sm font-bold tracking-tight text-whart-text">
            {t("appName")}
          </span>
        </div>

        <div className="flex items-center gap-4">
          <div
            aria-label={t("connectedProfile", { name: displayName })}
            className="flex items-center gap-2"
          >
            {profile ? (
              <TeamMemberInitialsAvatar
                id={profile.id}
                firstName={profile.first_name}
                lastName={profile.last_name}
              />
            ) : (
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10">
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="h-4 w-4"
                >
                  <circle cx="12" cy="8" r="3.5" />
                  <path
                    d="M5 20c0-3.314 3.582-6 7-6s7 2.686 7 6"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            )}
            <div className="flex flex-col">
              <span className="text-sm font-medium leading-tight text-whart-text">
                {displayName}
              </span>
              {roleLabel ? (
                <span className="text-xs leading-tight text-neutral-500">
                  {roleLabel}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              void handleLogout();
            }}
            className="text-sm text-whart-text transition-colors hover:text-neutral-600"
          >
            {t("logout")}
          </button>
        </div>
      </div>

      <div className="hidden border-t border-black/10 lg:block">
        <div className="px-4 lg:px-6">
          <TopNav pathname={pathname} />
        </div>
      </div>

      {isMobileNavOpen ? (
        <div className="border-t border-black/10 lg:hidden">
          <TopNav
            pathname={pathname}
            orientation="vertical"
            onNavigate={onNavClick}
          />
        </div>
      ) : null}
    </header>
  );
}
